/**
 * QuickRecordPage — the landing screen when a worker scans a field QR sticker/sign
 * (recordLink → /r/plant/:id or /r/block/:id, login-gated by RequireAuth). A focused,
 * mobile-first sheet to record picking or other activity against that plant or block.
 *
 *   plant: Record harvest (weight/units/grade) · Log activity
 *   block: Record fruit picked · Log activity · Roll up today's plant-level pickings
 *
 * Recording reuses existing endpoints (harvestsApi.create, activityApi.commit); the
 * harvest auto-links block_id (from the plant, or the block itself) so the block
 * roll-up reporting is automatic.
 */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Check, ChevronLeft, Leaf, Grid3x3, ClipboardList, Scale, CloudOff } from 'lucide-react'
import { plantsApi, blocksApi, prefsApi, type ParsedEvent } from '../lib/api'
import { submitOrQueue, useOnlineStatus } from '../lib/offlineSync'
import { weightUnit, inputToKg, formatWeight, type MeasurementSystem } from '../lib/units'

const ACTIVITY_ACTIONS = ['observation', 'spray', 'prune', 'fertilize', 'irrigate', 'thin', 'custom'] as const

const C = {
  bg: 'var(--color-bg)', card: 'var(--color-bg-card)', border: 'var(--color-border)',
  text: 'var(--color-text)', muted: 'var(--color-text-muted)', faint: 'var(--color-text-faint)',
  primary: 'var(--color-primary)',
}
const todayISO = () => new Date().toISOString()
const todayDate = () => new Date().toISOString().slice(0, 10)

export default function QuickRecordPage({ kind }: { kind: 'plant' | 'block' }) {
  const { id = '' } = useParams()
  const nav = useNavigate()
  const online = useOnlineStatus()
  const { data: prefs } = useQuery({ queryKey: ['user-preferences'], queryFn: () => prefsApi.get().then(r => r.data) })
  const system: MeasurementSystem = (prefs?.measurement_system as MeasurementSystem) ?? 'imperial'

  const plantQ = useQuery({ queryKey: ['plant', id], queryFn: () => plantsApi.get(id).then(r => r.data), enabled: kind === 'plant' })
  const blockQ = useQuery({ queryKey: ['block', id], queryFn: () => blocksApi.get(id).then(r => r.data), enabled: kind === 'block' })
  const dailyQ = useQuery({ queryKey: ['block-daily', id], queryFn: () => blocksApi.dailyPickings(id).then(r => r.data), enabled: kind === 'block' })

  const loading = kind === 'plant' ? plantQ.isLoading : blockQ.isLoading
  const notFound = kind === 'plant' ? plantQ.isError : blockQ.isError
  const title = kind === 'plant' ? (plantQ.data?.label || 'Plant') : (blockQ.data?.name || 'Block')
  const subtitle = kind === 'plant'
    ? [plantQ.data?.variety?.name, plantQ.data?.block_id ? 'in a block' : null].filter(Boolean).join(' · ')
    : `${blockQ.data?.tracking_mode ?? ''} · ${blockQ.data?.effective_plant_count ?? blockQ.data?.plant_count ?? 0} plants`

  return (
    <main id="main-content" style={{ minHeight: '100dvh', background: C.bg, color: C.text, paddingBottom: 'calc(24px + env(safe-area-inset-bottom))' }}>
      <header style={{ position: 'sticky', top: 0, zIndex: 5, background: C.card, borderBottom: `1px solid ${C.border}`, padding: 'calc(12px + env(safe-area-inset-top)) 14px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => nav('/')} aria-label="Home" style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', display: 'flex' }}><ChevronLeft size={22} /></button>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.primary }}>
          {kind === 'plant' ? <Leaf size={14} /> : <Grid3x3 size={14} />} {kind === 'plant' ? 'Plant' : 'Block'}
        </span>
      </header>

      {!online && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '8px 14px', background: 'color-mix(in srgb, var(--color-primary) 16%, transparent)', color: C.primary, fontSize: 13, fontWeight: 600 }}>
          <CloudOff size={15} /> Offline — records save on this device and sync when you reconnect.
        </div>
      )}

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '16px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {loading && <div style={{ color: C.muted, fontSize: 14 }}>Loading…</div>}
        {notFound && <div style={{ color: '#E5533C', fontSize: 14 }}>Couldn’t load this {kind}. It may have been removed, or you may not have access.</div>}
        {!loading && !notFound && (
          <>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{title}</h1>
              {subtitle && <p style={{ margin: '2px 0 0', fontSize: 13, color: C.muted }}>{subtitle}</p>}
            </div>

            {kind === 'block' && dailyQ.data && dailyQ.data.events > 0 && (
              <div style={{ padding: '10px 12px', borderRadius: 10, background: 'color-mix(in srgb, var(--color-primary) 8%, transparent)', border: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 12, color: C.muted }}>Today’s pickings rolled up to this block</div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{formatWeight(dailyQ.data.total_kg, system)} · {dailyQ.data.plants_picked} tree{dailyQ.data.plants_picked === 1 ? '' : 's'}</div>
              </div>
            )}

            <HarvestAction kind={kind} system={system}
              plantId={kind === 'plant' ? id : undefined}
              blockId={kind === 'plant' ? (plantQ.data?.block_id || undefined) : id}
              varietyId={kind === 'plant' ? (plantQ.data?.variety_id || undefined) : (blockQ.data?.variety_id || undefined)}
              onDone={() => { if (kind === 'block') dailyQ.refetch() }}
            />

            <ActivityAction resourceLabel={title} onDone={() => { /* logged */ }} />

            {kind === 'block' && <RollupView report={dailyQ.data} onRefresh={() => dailyQ.refetch()} system={system} loading={dailyQ.isFetching} />}
          </>
        )}
      </div>
    </main>
  )
}

// ── Record harvest ───────────────────────────────────────────────────────────
function HarvestAction({ kind, system, plantId, blockId, varietyId, onDone }: {
  kind: 'plant' | 'block'; system: MeasurementSystem
  plantId?: string; blockId?: string; varietyId?: string; onDone: () => void
}) {
  const [open, setOpen] = useState(false)
  const [weight, setWeight] = useState('')
  const [units, setUnits] = useState('')
  const [grade, setGrade] = useState('')
  const [saved, setSaved] = useState(false)
  const [queued, setQueued] = useState(false)

  const save = useMutation({
    mutationFn: () => submitOrQueue('harvest', {
      plant_id: plantId, block_id: blockId, variety_id: varietyId,
      harvested_at: todayDate(),
      weight_kg: weight ? inputToKg(parseFloat(weight), system) : undefined,
      unit_count: units ? parseInt(units, 10) : undefined,
      grade: grade || undefined,
    }, `Harvest${weight ? ` · ${weight} ${weightUnit(system)}` : ''}`),
    onSuccess: (res) => { setSaved(true); setQueued(res.queued); setWeight(''); setUnits(''); setGrade(''); onDone() },
  })

  return (
    <ActionCard icon={<Scale size={18} />} title={kind === 'block' ? 'Record fruit picked' : 'Record harvest'}
      subtitle={kind === 'block' ? 'Log a harvest for this block today' : 'Log fruit picked from this tree today'}
      open={open} onToggle={() => { setOpen(o => !o); setSaved(false) }}>
      {saved ? (
        <Done label={queued ? 'Harvest saved offline — will sync' : 'Harvest recorded'} queued={queued} onAgain={() => { setSaved(false); setOpen(true) }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Row>
            <Field label={`Weight (${weightUnit(system)})`}><input inputMode="decimal" value={weight} onChange={e => setWeight(e.target.value)} placeholder="0" style={inp} /></Field>
            <Field label="Count (optional)"><input inputMode="numeric" value={units} onChange={e => setUnits(e.target.value)} placeholder="—" style={inp} /></Field>
          </Row>
          <Field label="Grade (optional)">
            <select value={grade} onChange={e => setGrade(e.target.value)} style={inp}>
              <option value="">—</option>{['A', 'B', 'C', 'Cull', 'Export'].map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          {save.isError && <div style={errStyle}>Couldn’t save — try again.</div>}
          <button onClick={() => save.mutate()} disabled={save.isPending || (!weight && !units)} style={primaryBtn(save.isPending || (!weight && !units))}>
            {save.isPending ? 'Saving…' : 'Save harvest'}
          </button>
        </div>
      )}
    </ActionCard>
  )
}

// ── Log activity ───────────────────────────────────────────────────────────
function ActivityAction({ resourceLabel, onDone }: { resourceLabel: string; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [action, setAction] = useState<string>('observation')
  const [note, setNote] = useState('')
  const [saved, setSaved] = useState(false)
  const [queued, setQueued] = useState(false)
  const save = useMutation({
    mutationFn: () => {
      const ev: ParsedEvent = {
        action_type: action, occurred_at: todayISO(),
        description: note || action, resource_label: resourceLabel, confidence: 1,
      }
      return submitOrQueue('activity', [ev], `${action}${resourceLabel ? ` · ${resourceLabel}` : ''}`)
    },
    onSuccess: (res) => { setSaved(true); setQueued(res.queued); setNote(''); onDone() },
  })
  return (
    <ActionCard icon={<ClipboardList size={18} />} title="Log activity" subtitle="Spray, prune, observation, …"
      open={open} onToggle={() => { setOpen(o => !o); setSaved(false) }}>
      {saved ? (
        <Done label={queued ? 'Activity saved offline — will sync' : 'Activity logged'} queued={queued} onAgain={() => { setSaved(false); setOpen(true) }} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Field label="Activity">
            <select value={action} onChange={e => setAction(e.target.value)} style={inp}>
              {ACTIVITY_ACTIONS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
          <Field label="Note (optional)"><textarea value={note} onChange={e => setNote(e.target.value)} rows={2} placeholder="What did you do / observe?" style={{ ...inp, resize: 'vertical' }} /></Field>
          {save.isError && <div style={errStyle}>Couldn’t save — try again.</div>}
          <button onClick={() => save.mutate()} disabled={save.isPending} style={primaryBtn(save.isPending)}>{save.isPending ? 'Saving…' : 'Log it'}</button>
        </div>
      )}
    </ActionCard>
  )
}

// ── Block roll-up view ───────────────────────────────────────────────────────
function RollupView({ report, onRefresh, system, loading }: {
  report: import('../lib/api').DailyPickingsReport | undefined
  onRefresh: () => void; system: MeasurementSystem; loading: boolean
}) {
  const [open, setOpen] = useState(false)
  return (
    <ActionCard icon={<Grid3x3 size={18} />} title="Today’s pickings (roll-up)" subtitle="Fruit picked in this block today, by tree"
      open={open} onToggle={() => setOpen(o => !o)}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <button onClick={onRefresh} disabled={loading} style={{ alignSelf: 'flex-start', fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 7, background: 'color-mix(in srgb, var(--color-primary) 13%, transparent)', border: `1px solid color-mix(in srgb, var(--color-primary) 33%, transparent)`, color: C.primary, cursor: 'pointer' }}>
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
        {!report || report.events === 0 ? (
          <div style={{ fontSize: 13, color: C.muted }}>No pickings recorded for this block today yet. Scan a tree’s QR and record a harvest — it rolls up here automatically.</div>
        ) : (
          <>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{formatWeight(report.total_kg, system)} total · {report.plants_picked} tree{report.plants_picked === 1 ? '' : 's'} · {report.events} record{report.events === 1 ? '' : 's'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {report.by_plant.slice(0, 30).map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.text, padding: '3px 0', borderBottom: `1px solid ${C.border}` }}>
                  <span>{p.plant_label || 'Unlabeled tree'}</span><span style={{ color: C.muted }}>{formatWeight(p.weight_kg, system)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </ActionCard>
  )
}

// ── Shared bits ───────────────────────────────────────────────────────────
const inp: React.CSSProperties = { width: '100%', padding: '10px 12px', fontSize: 16, borderRadius: 9, background: C.bg, border: `1px solid ${C.border}`, color: C.text, outline: 'none', boxSizing: 'border-box' }
const errStyle: React.CSSProperties = { fontSize: 12, color: '#E5533C' }
const primaryBtn = (busy: boolean): React.CSSProperties => ({ padding: '12px 0', borderRadius: 9, fontSize: 15, fontWeight: 700, background: C.primary, border: 'none', color: '#0E1209', cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1 })

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>{children}</div>
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ display: 'block' }}><span style={{ display: 'block', fontSize: 12, color: C.muted, marginBottom: 4 }}>{label}</span>{children}</label>
}
function Done({ label, onAgain, queued }: { label: string; onAgain: () => void; queued?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '8px 0' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 15, fontWeight: 700, color: queued ? C.primary : '#4ADE80' }}>
        {queued ? <CloudOff size={18} /> : <Check size={18} />} {label}
      </div>
      <button onClick={onAgain} style={{ fontSize: 13, fontWeight: 600, padding: '8px 16px', borderRadius: 8, background: 'transparent', border: `1px solid ${C.border}`, color: C.text, cursor: 'pointer' }}>Record another</button>
    </div>
  )
}
function ActionCard({ icon, title, subtitle, open, onToggle, children }: {
  icon: React.ReactNode; title: string; subtitle: string; open: boolean; onToggle: () => void; children: React.ReactNode
}) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 12, background: C.card, overflow: 'hidden' }}>
      <button onClick={onToggle} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 14px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', color: C.text }}>
        <span style={{ display: 'inline-flex', color: C.primary }}>{icon}</span>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'block', fontSize: 15, fontWeight: 700 }}>{title}</span>
          <span style={{ display: 'block', fontSize: 12, color: C.muted }}>{subtitle}</span>
        </span>
        <span style={{ color: C.faint, fontSize: 18 }}>{open ? '−' : '+'}</span>
      </button>
      {open && <div style={{ padding: '0 14px 14px' }}>{children}</div>}
    </div>
  )
}
